import { createBaseAccountSDK } from '@base-org/account';
import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { DYNAMIC_ICONIC_SPRITE_URL } from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';

import type {
  EIP1193Provider,
  EvmWalletProvider,
} from '../../EvmWalletProvider.types';
import { createWalletProviderFromEIP1193Provider } from '../../utils/createWalletProviderFromEIP1193Provider';
import { getEvmNetworkIds } from '../../utils/getEvmNetworkIds/getEvmNetworkIds';
import type { AddBaseAccountEvmExtensionOptions } from '../baseAccount.types';
import { getBaseAccountAppMetadata } from '../utils/getBaseAccountAppMetadata/getBaseAccountAppMetadata';

const BASE_ACCOUNT_DISPLAY_NAME = 'Base Account';
const BASE_ACCOUNT_ICON = `${DYNAMIC_ICONIC_SPRITE_URL}#base`;

type BaseAccountSDKProvider = ReturnType<
  ReturnType<typeof createBaseAccountSDK>['getProvider']
>;

/**
 * Builds an `EvmWalletProvider` backed by `@base-org/account`. Used internally
 * by `addBaseAccountEvmExtension`; not part of the public API.
 *
 * The underlying `@base-org/account` SDK is constructed lazily on the first
 * EIP-1193 call. This avoids reading `client.projectSettings` (via
 * `getEvmNetworkIds`) at extension-registration time — which would throw if
 * the extension is added before `initializeClient` resolves project settings.
 *
 * Classification matches dynamic-auth's mapping for the popup-SDK flow:
 * `WalletProviderEnum.CustodialService`. The browser-extension path is owned
 * by `addEIP6963Extension` / `addEvmWindowInjectedExtension`.
 *
 * @param options - Optional Base Account preference.
 * @param client - The Dynamic client whose metadata + networks drive the SDK.
 * @not-instrumented
 */
export const createBaseAccountEvmWalletProvider = (
  { preference }: AddBaseAccountEvmExtensionOptions,
  client: DynamicClient
): EvmWalletProvider => {
  let cachedProvider: BaseAccountSDKProvider | undefined;

  const getOrCreateProvider = (): BaseAccountSDKProvider => {
    if (cachedProvider) {
      return cachedProvider;
    }
    const { appName, appLogoUrl } = getBaseAccountAppMetadata(client);
    cachedProvider = createBaseAccountSDK({
      appChainIds: getEvmNetworkIds(client),
      appLogoUrl,
      appName,
      preference,
    }).getProvider();
    return cachedProvider;
  };

  // EIP-1193 façade that defers SDK construction until first interaction and
  // bridges the two `on` event vocabularies — `@base-org/account` emits the
  // canonical `disconnect`, while our local `EIP1193Provider` consumer expects
  // `disconnected`. We forward each event explicitly so the divergence is
  // visible in source rather than smoothed over by a blanket cast.
  const lazyEip1193: EIP1193Provider = {
    on: (event, listener) => {
      const provider = getOrCreateProvider();
      if (event === 'accountsChanged') {
        provider.on('accountsChanged', listener as (a: string[]) => void);
      } else if (event === 'chainChanged') {
        provider.on('chainChanged', listener as (c: string) => void);
      } else {
        provider.on('disconnect', () => (listener as () => void)());
      }
    },
    removeAllListeners: () => cachedProvider?.removeAllListeners(),
    request: <T = unknown>(args: { method: string; params: unknown[] }) =>
      getOrCreateProvider().request(args) as Promise<T>,
  };

  return createWalletProviderFromEIP1193Provider(
    {
      displayName: BASE_ACCOUNT_DISPLAY_NAME,
      eip1193: lazyEip1193,
      icon: BASE_ACCOUNT_ICON,
      walletProviderType: WalletProviderEnum.CustodialService,
    },
    client
  );
};
